'use client';

import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ElementRef,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { ChevronLeft } from '../../../icons';
import { cn } from '../../../lib/utils';
import { DrawerClose, DrawerTitle } from '../../drawer/drawer';
import { IconButton, type IconButtonProps } from '../../icon-button/icon-button';
import { useDrawerMenuPanel } from './drawer-menu-context';

export type DrawerMenuHandleProps = ComponentPropsWithoutRef<'div'>;

export const DrawerMenuHandle = ({ className, ...props }: DrawerMenuHandleProps) => (
  <div
    aria-hidden
    data-slot="drawer-menu-handle"
    className={cn('h-1 w-[60px] shrink-0 self-center rounded-full bg-space-15', className)}
    {...props}
  />
);

export type DrawerMenuTitleProps = ComponentPropsWithoutRef<typeof DrawerTitle>;

// the sheet's accessible name; truncates by default, `whitespace-normal` lets it wrap
export const DrawerMenuTitle = forwardRef<ElementRef<typeof DrawerTitle>, DrawerMenuTitleProps>(
  ({ className, ...props }, ref) => (
    <DrawerTitle
      ref={ref}
      data-slot="drawer-menu-title"
      // truncation spelled out so a consumer `whitespace-normal` merges over it
      className={cn(
        'min-w-0 flex-1 overflow-hidden text-left text-ellipsis whitespace-nowrap',
        className,
      )}
      {...props}
    />
  ),
);
DrawerMenuTitle.displayName = 'DrawerMenuTitle';

export type DrawerMenuBackButtonProps = Omit<IconButtonProps, 'icon'>;

// renders on the root too, so a custom header shows it only in submenus
export const DrawerMenuBackButton = forwardRef<HTMLButtonElement, DrawerMenuBackButtonProps>(
  ({ onClick, ...props }, ref) => {
    const { back } = useDrawerMenuPanel();

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
      onClick?.(event);
      back();
    };

    return (
      <IconButton
        ref={ref}
        icon={ChevronLeft}
        variant="grey"
        size="sm"
        aria-label="Back"
        onClick={handleClick}
        {...props}
      />
    );
  },
);
DrawerMenuBackButton.displayName = 'DrawerMenuBackButton';

export type DrawerMenuCloseButtonProps = ComponentPropsWithoutRef<typeof DrawerClose>;

export const DrawerMenuCloseButton = (props: DrawerMenuCloseButtonProps) => (
  <DrawerClose aria-label="Close" {...props} />
);

export type DrawerMenuHeaderProps = {
  /** Title of the root panel. Submenus title themselves from the row that opens them. */
  title: ReactNode;
  /**
   * Shows the root title. When `false` it is only visually hidden: the title is still
   * required, as it stays the sheet's accessible name. Submenu titles always show.
   */
  showTitle?: boolean;
  className?: string;
  classNames?: {
    /** It truncates by default; `whitespace-normal` lets it wrap. */
    title?: string;
  };
};

/**
 * The standard header: drag handle, then the root title and Close, or Back, the
 * submenu title and Close. For a one-off header, build one from the parts and
 * `useDrawerMenuPanel` and render it in place of this one.
 */
export const DrawerMenuHeader = ({
  title,
  showTitle = true,
  className,
  classNames,
}: DrawerMenuHeaderProps) => {
  const { isSubmenu, title: submenuTitle } = useDrawerMenuPanel();

  return (
    <div data-slot="drawer-menu-header" className={cn('flex flex-col gap-3 p-4', className)}>
      <DrawerMenuHandle />

      {isSubmenu ? (
        <div className="flex items-center">
          <div className="w-8 shrink-0">
            <DrawerMenuBackButton />
          </div>
          <DrawerMenuTitle className={cn('px-2 text-center', classNames?.title)}>
            {submenuTitle}
          </DrawerMenuTitle>
          <div className="flex w-8 shrink-0 justify-end">
            <DrawerMenuCloseButton />
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-end gap-2">
          <DrawerMenuTitle className={cn(!showTitle && 'sr-only', classNames?.title)}>
            {title}
          </DrawerMenuTitle>
          <DrawerMenuCloseButton />
        </div>
      )}
    </div>
  );
};
